import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { getModelDisplayName } from '../../constants/models';

// Model colors
const MODEL_COLORS = ['#00BCD4', '#32CD32', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981', '#6B8E6B'];

/**
 * ComparisonWinRateChart - Displays which models win most in comparisons
 * A model "wins" when it's both faster AND cheaper
 */
const ComparisonWinRateChart = ({ comparisons, loading }) => {
  // Calculate win rates
  const { chartData, totalComparisons, topWinner } = useMemo(() => {
    if (!comparisons || comparisons.length === 0) {
      return { chartData: [], totalComparisons: 0, topWinner: null };
    }

    const modelWins = {};
    let tieCount = 0;

    comparisons.forEach(comp => {
      const latency1 = comp.latency_ms_1 || 0;
      const latency2 = comp.latency_ms_2 || 0;
      const cost1 = comp.estimated_cost_1 || 0;
      const cost2 = comp.estimated_cost_2 || 0;

      // Model 1 wins if faster AND cheaper (or equal in one and better in other)
      const model1Faster = latency1 < latency2;
      const model1Cheaper = cost1 < cost2;
      const model2Faster = latency2 < latency1;
      const model2Cheaper = cost2 < cost1;

      let winner = null;
      
      if (model1Faster && model1Cheaper) {
        winner = comp.model_1;
      } else if (model2Faster && model2Cheaper) {
        winner = comp.model_2;
      } else if (model1Faster && cost1 === cost2) {
        winner = comp.model_1;
      } else if (model2Faster && cost1 === cost2) {
        winner = comp.model_2;
      } else if (model1Cheaper && latency1 === latency2) {
        winner = comp.model_1;
      } else if (model2Cheaper && latency1 === latency2) {
        winner = comp.model_2;
      } else {
        // Mixed results or tie
        tieCount++;
      }

      if (winner) {
        if (!modelWins[winner]) {
          modelWins[winner] = 0;
        }
        modelWins[winner]++;
      }
    });

    // Convert to chart data
    const data = Object.entries(modelWins)
      .map(([model, wins]) => ({
        model: getModelDisplayName(model),
        fullModel: getModelDisplayName(model),
        wins,
        percentage: ((wins / comparisons.length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.wins - a.wins);

    // Add ties/mixed if any
    if (tieCount > 0) {
      data.push({
        model: 'Mixed',
        fullModel: 'Mixed Results',
        wins: tieCount,
        percentage: ((tieCount / comparisons.length) * 100).toFixed(1),
        isTie: true
      });
    }

    return {
      chartData: data.slice(0, 6), // Top 6
      totalComparisons: comparisons.length,
      topWinner: data.length > 0 && !data[0].isTie ? data[0] : null
    };
  }, [comparisons]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 border border-primary-500/30 backdrop-blur-sm">
          <p className="text-sm font-semibold text-text-primary">
            {data.fullModel}
          </p>
          <p className="text-xs mt-1 text-primary-500">
            {data.wins} wins ({data.percentage}%)
          </p>
          {!data.isTie && (
            <p className="text-xs mt-1 text-text-tertiary">
              Faster & cheaper in comparisons
            </p>
          )}
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  if (loading) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-32 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] flex items-center justify-center">
          <div className="w-32 h-32 rounded-full animate-pulse bg-primary-500/5 border-8 border-primary-500/10" />
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Win Rate
        </h3>
        <div className="h-[200px] flex flex-col items-center justify-center">
          <svg className="w-10 h-10 mb-2 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-text-tertiary">No comparison data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Win Rate
          </h3>
          <p className="text-xs mt-0.5 text-text-tertiary">
            Faster & cheaper wins
          </p>
        </div>
        {topWinner && (
          <div className="text-right">
            <span className="text-xs text-text-tertiary">Top: </span>
            <span className="text-xs font-semibold text-primary-500">
              {topWinner.model}
            </span>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-4">
        {/* Donut Chart */}
        <div className="h-[180px] w-[180px] flex-shrink-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="wins"
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.isTie ? '#6B8E6B' : MODEL_COLORS[index % MODEL_COLORS.length]}
                    style={{ 
                      filter: `drop-shadow(0 0 6px ${entry.isTie ? '#6B8E6B' : MODEL_COLORS[index % MODEL_COLORS.length]}40)` 
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Center text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-xl font-bold text-text-primary">
                {totalComparisons}
              </div>
              <div className="text-xs text-text-tertiary">
                total
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2">
          {chartData.map((item, index) => (
            <div key={item.model} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm"
                  style={{ background: item.isTie ? '#6B8E6B' : MODEL_COLORS[index % MODEL_COLORS.length] }}
                />
                <span className="text-xs truncate text-text-secondary max-w-[100px]">
                  {item.model}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-text-primary">
                  {item.wins}
                </span>
                <span className="text-xs text-text-tertiary">
                  ({item.percentage}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

ComparisonWinRateChart.propTypes = {
  comparisons: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool
};

export default ComparisonWinRateChart;
